import {
  OutgoingQuotationDto,
  OutgoingQuotationState,
  QuotationBoMItemActivityLineDto,
  QuotationLineDto,
  SaveOutgoingQuotationCommandRequest,
  SaveOutgoingQuotationCommandResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { GetContext } from 'store/storeUtils'
import { OutgoingQuotationsController } from './OutgoingQuotationsController'

export class ProjectQuotationsController extends OutgoingQuotationsController {
  private readonly projectId: string

  constructor() {
    super()
    this.projectId = GetContext().projectId
  }

  public async getOutgoingQuotation(): Promise<OutgoingQuotationDto> {
    const existingResponse = (await this.list())
      .filter((quotation) => quotation.supplierProjectId === this.projectId)
      .sort((left, right) => {
        const statePriority =
          Number(left.state === OutgoingQuotationState.Draft) -
          Number(right.state === OutgoingQuotationState.Draft)

        return (
          statePriority ||
          new Date(right.dateChanged ?? right.dateCreated ?? 0).getTime() -
            new Date(left.dateChanged ?? left.dateCreated ?? 0).getTime()
        )
      })[0]

    if (existingResponse) {
      if (
        existingResponse.state === OutgoingQuotationState.Draft &&
        existingResponse.quotation
      ) {
        const current = await this.draft(this.projectId)
        existingResponse.quotation = {
          ...existingResponse.quotation,
          lines: refreshCalculatedPrices(
            existingResponse.quotation.lines ?? [],
            current.quotation.lines ?? [],
          ),
        }
      }
      return existingResponse
    }

    const draft = await this.draft(this.projectId)
    return {
      id: draft.quotation.id,
      supplierProjectId: this.projectId,
      state: OutgoingQuotationState.Draft,
      quotation: draft.quotation,
    }
  }

  public saveOutgoingQuotation(
    request: SaveOutgoingQuotationCommandRequest,
  ): Promise<SaveOutgoingQuotationCommandResponse> {
    return this.save(request.outgoingQuotationId, request)
  }

  public sendOutgoingQuotation(quotationId: string): Promise<void> {
    return this.send(quotationId, this.projectId)
  }

  public deleteOutgoingQuotation(quotationId: string): Promise<void> {
    return this.delete(quotationId)
  }
}

export function refreshCalculatedPrices(
  persisted: QuotationLineDto[],
  calculated: QuotationLineDto[],
): QuotationLineDto[] {
  const calculatedById = new Map(calculated.map((line) => [line.id, line]))
  return persisted.map((line) => {
    const current = calculatedById.get(line.id)
    if (!current) return line
    const previousSuggestion = selectedActivityTotal(line)
    const currentSuggestion = selectedActivityTotal(current)
    const previousSupplemental =
      (line.salesPrice?.value ?? 0) - previousSuggestion
    const currentSupplemental =
      (current.salesPrice?.value ?? 0) - currentSuggestion
    const customerPriceWasCalculated =
      Math.abs(previousSupplemental - currentSupplemental) < 0.000001
    const currentBomItems = new Map(
      (current.quotationBomItemLines ?? []).map((item) => [
        item.boMItemId,
        item,
      ]),
    )
    return {
      ...line,
      salesPrice: customerPriceWasCalculated
        ? current.salesPrice
        : line.salesPrice,
      quotationBomItemLines: (line.quotationBomItemLines ?? []).map((item) => {
        const currentItem = currentBomItems.get(item.boMItemId)
        if (!currentItem) return item
        return {
          ...item,
          activities: (item.activities ?? []).map((activity) => {
            const currentActivity = findActivity(
              activity,
              currentItem.activities ?? [],
            )
            return currentActivity
              ? {
                  ...activity,
                  salesPrice: currentActivity.salesPrice,
                  discountValue: currentActivity.discountValue,
                  surchargeValue: currentActivity.surchargeValue,
                  discountRatio: currentActivity.discountRatio,
                  surchargeRatio: currentActivity.surchargeRatio,
                }
              : activity
          }),
        }
      }),
    }
  })
}

function selectedActivityTotal(line: QuotationLineDto): number {
  return (line.quotationBomItemLines ?? [])
    .flatMap((item) => item.activities ?? [])
    .filter((activity) => activity.includeInQuotation !== false)
    .reduce((sum, activity) => sum + (activity.salesPrice?.value ?? 0), 0)
}

function findActivity(
  activity: QuotationBoMItemActivityLineDto,
  candidates: QuotationBoMItemActivityLineDto[],
) {
  return candidates.find(
    (candidate) =>
      candidate.boMItemActivityId === activity.boMItemActivityId ||
      candidate.originalActivityId === activity.originalActivityId,
  )
}
